import React from 'react'
import styled, { css, keyframes } from 'styled-components'
import { lighten, adjustHue, darken } from 'polished'
import characters from '../../img/characters.webp'
import Spacer from '../../lib/spacer'
import StoreLinks from '../../common/store_links'
import RolesBanner from './roles_banner'
import { large, medium, small } from '../../lib/media_query'
import { useTranslation } from '../../lib/i18n'
import HeroVideo, { useHeroReel } from './hero_video'

const Wrapper = styled.div`
    background-color: ${(props) => props.theme.palette.brand.blue};
    background: linear-gradient(
        160deg,
        ${(props) => darken(0.06, adjustHue(-15, props.theme.palette.brand.blue))} 0%,
        ${(props) => props.theme.palette.brand.blue} 50%,
        ${(props) => lighten(0.04, adjustHue(8, props.theme.palette.brand.blue))} 100%
    );
    color: white;
    position: relative;
    overflow: hidden;

    html.hero-video & {
        background-color: #555960;
        background: linear-gradient(160deg, #4b4e54 0%, #686d75 50%, #52565c 100%);
    }

    > .main {
        position: relative;
        z-index: 2;
    }

    h1 {
        text-shadow: 0 2px 20px rgba(0, 0, 0, 0.15);
    }

    p.lead {
        color: white;
    }

    &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        width: 100%;
        height: 30px;
        background: white;
        clip-path: ellipse(70% 100% at 50% 100%);
        z-index: 3;
    }
`

const land = keyframes`
    from {
        opacity: 0;
        transform: scale(1.12);
        filter: blur(6px);
        letter-spacing: 0.04em;
    }
    to {
        opacity: 1;
        transform: none;
        filter: none;
        letter-spacing: normal;
    }
`

const focus = keyframes`
    from {
        opacity: 0;
        transform: translateY(36px);
        filter: blur(10px);
    }
    to {
        opacity: 1;
        transform: none;
        filter: none;
    }
`

const appear = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`

const entranceStyle = (frames, duration, delay, easing) => css`
    ${large(css`transform-origin: left center;`)}

    ${(props) => {
        if (props.skipAnimation) {
            return css`
                opacity: 1;
                transform: none;
                filter: none;
            `
        }
        if (props.playing) {
            return css`
                animation: ${frames} ${duration}s ${easing} ${delay}s both;
            `
        }
        return css`
            opacity: 0;
        `
    }}
`

const TitleEntrance = styled.div`
    ${entranceStyle(land, 1.1, 1.8, 'cubic-bezier(0.22, 1, 0.36, 1)')}
`

const LeadEntrance = styled.div`
    ${entranceStyle(focus, 1.0, 2.6, 'cubic-bezier(0.16, 1, 0.3, 1)')}
`

const ButtonsEntrance = styled.div`
    ${entranceStyle(appear, 0.85, 3.5, 'ease')}
`

// Only shown for reduced motion browsers
const CharactersImage = styled.img`
    display: block;
    margin: 0 auto 20px;

    ${large(css`width: 390px;`)}
    ${medium(css`width: 320px;`)}
    ${small(css`max-width: 260px; width: 100%;`)}
`

export default () => {
    const { t } = useTranslation(undefined, { keyPrefix: 'home.hero' })
    const { reel, playing, hasSeenIntro, prefersReducedMotion } = useHeroReel()
    const skipEntrance = hasSeenIntro || prefersReducedMotion

    return (
        <>
            <Wrapper className={playing ? 'playing' : undefined}>
                <HeroVideo
                    reel={reel}
                    playing={playing}
                    hasSeenIntro={hasSeenIntro}
                    prefersReducedMotion={prefersReducedMotion}
                />
                <Spacer size={120} className="show-for-large" />
                <Spacer size={80} className="show-for-medium-only" />
                <Spacer size={50} className="show-for-small-only" />
                <div className="main grid-container">
                    <div className="grid-x align-center-middle">
                        {prefersReducedMotion ? (
                            <div className="cell medium-12 large-auto large-order-1">
                                <div className="grid-x align-center align-middle max-height">
                                    <div className="cell small-12 medium-8 large-12">
                                        <CharactersImage src={characters} alt={t('charactersAlt')} />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div
                                className="cell large-auto large-order-1 show-for-large"
                                aria-hidden="true"
                                style={{ minHeight: 320 }}
                            />
                        )}
                        <div className="cell medium-9 large-6 text-center large-text-left">
                            <div className="grid-y align-center max-height">
                                <div className="cell shrink">
                                    <Spacer size={20} className="hide-for-large" />
                                    <TitleEntrance skipAnimation={skipEntrance} playing={playing}>
                                        <h1>
                                            {t('titleLine1')}
                                            <br /> {t('titleLine2')}
                                        </h1>
                                    </TitleEntrance>
                                    <Spacer size={15} />
                                    <LeadEntrance skipAnimation={skipEntrance} playing={playing}>
                                        <p className="lead">{t('lead')}</p>
                                    </LeadEntrance>
                                    <Spacer size={20} />
                                </div>
                                <div className="cell">
                                    <ButtonsEntrance
                                        skipAnimation={skipEntrance}
                                        playing={playing}
                                        className="grid-x small-align-center medium-align-center large-align-left"
                                    >
                                        <StoreLinks light />
                                    </ButtonsEntrance>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Spacer size={180} className="show-for-large" />
                <Spacer size={120} className="show-for-medium-only" />
                <Spacer size={80} className="show-for-small-only" />
            </Wrapper>
            {/* <RolesBanner /> */}
        </>
    )
}
